import { useState, useRef, useEffect, useCallback } from 'react';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { api } from '@/services/api';
import { useLanguage, translateForLanguage, type Language } from '@/contexts/LanguageContext';
import type { Company, JobPosition, JobPosting } from '@/types';

type FormState = 'loading' | 'not_found' | 'closed' | 'form' | 'success';

/**
 * Sprint 6 (S6.D) — best-effort LinkedIn URL validator.
 *
 * Accepts:
 *   - https://[www.|it.]linkedin.com/in/<slug>
 *   - linkedin.com/in/<slug> (no scheme)
 *   - Bare slug ("marco-rossi") — common when candidates copy-paste from
 *     their profile header.
 * Rejects:
 *   - "I don't have one" / random sentences
 *   - URLs from other domains (github, twitter)
 *   - Empty/whitespace-only
 */
function isLikelyLinkedInUrl(raw: string): boolean {
  const v = raw.trim().toLowerCase();
  if (!v) return false;
  if (v.includes(' ')) return false;
  if (v.includes('linkedin.com/in/')) return true;
  // Bare slug: 3+ chars, only [a-z0-9-_], no dots → assume LinkedIn vanity.
  if (/^[a-z0-9][a-z0-9-_]{2,99}$/.test(v) && !v.includes('.')) return true;
  return false;
}

export interface UsePublicApplicationReturn {
  pageState: FormState;
  pageLanguage: Language;
  tPage: (key: string, params?: Record<string, string | number>) => string;
  submitting: boolean;
  errors: Record<string, string>;
  serverError: string;
  company: Company | null;
  position: JobPosition | null;
  posting: JobPosting | null;
  firstName: string;
  setFirstName: (v: string) => void;
  lastName: string;
  setLastName: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  phone: string;
  setPhone: (v: string) => void;
  linkedIn: string;
  setLinkedIn: (v: string) => void;
  cvFile: File | null;
  setCvFile: (v: File | null) => void;
  gdprConsent: boolean;
  setGdprConsent: (v: boolean) => void;
  aiConsent: boolean;
  setAiConsent: (v: boolean) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  handleFileDrop: (e: React.DragEvent) => void;
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function usePublicApplication(companySlug: string | undefined, postingSlug: string | undefined): UsePublicApplicationReturn {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t: tApp } = useLanguage();

  const [pageState, setPageState] = useState<FormState>('loading');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState('');

  const [company, setCompany] = useState<Company | null>(null);
  const [position, setPosition] = useState<JobPosition | null>(null);
  const [posting, setPosting] = useState<JobPosting | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [linkedIn, setLinkedIn] = useState('');
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [gdprConsent, setGdprConsent] = useState(false);
  const [aiConsent, setAiConsent] = useState(false);

  const pageLanguage: Language =
    (position?.outputLanguage === 'en' || position?.outputLanguage === 'it'
      ? position.outputLanguage
      : posting?.outputLanguage === 'en' || posting?.outputLanguage === 'it'
        ? posting.outputLanguage
        : 'it');
  const tApply = useCallback(
    (key: string, params?: Record<string, string | number>) =>
      translateForLanguage(pageLanguage, key, params),
    [pageLanguage],
  );
  const tPage = pageState === 'form' || pageState === 'success' ? tApply : tApp;

  useEffect(() => {
    if (!companySlug || !postingSlug) {
      setPageState('not_found');
      return;
    }
    api.public.getPosting(companySlug, postingSlug).then(data => {
      if (!data) { setPageState('not_found'); return; }
      setCompany(data.company);
      setPosition(data.position);
      setPosting(data.posting);
      setPageState(data.posting.isActive ? 'form' : 'closed');
    }).catch(() => setPageState('not_found'));
  }, [companySlug, postingSlug]);

  const validate = useCallback(() => {
    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs.firstName = tPage('apply.firstNameRequired');
    if (!lastName.trim()) errs.lastName = tPage('apply.lastNameRequired');
    if (!email.trim()) errs.email = tPage('apply.emailRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = tPage('apply.emailInvalid');

    // Sprint 6 (S6.C) — phone validation via libphonenumber-js. We only
    // check the format when the user actually provided a phone (or the
    // position requires it). The library accepts national + international
    // formats; the recruiter side stores whatever the candidate typed
    // (the BE schema is min(5).max(30) on the raw string), so we keep
    // the value verbatim — only the FE-side validation hint changes.
    if (position?.requirePhone && !phone.trim()) {
      errs.phone = tPage('apply.phoneRequired');
    } else if (phone.trim() && !isValidPhoneNumber(phone.trim(), 'IT')) {
      // Default region IT — the public posting page is geographically
      // anchored to the company's primary office. Most candidates apply
      // from Italian numbers; international ones still validate when
      // they include a +CC prefix.
      errs.phone = tPage('apply.phoneInvalid');
    }

    // Sprint 6 (S6.D) — LinkedIn URL validation. We accept either a full
    // https://www.linkedin.com/in/... URL or the bare slug ("marco-rossi").
    // Free-form text is the most common typo (recruiters get
    // "I don't have one" pasted in literally), so we surface a hint.
    if (position?.requireLinkedIn && !linkedIn.trim()) {
      errs.linkedIn = tPage('apply.linkedInRequired');
    } else if (linkedIn.trim() && !isLikelyLinkedInUrl(linkedIn.trim())) {
      errs.linkedIn = tPage('apply.linkedInInvalid');
    }

    if (!cvFile) errs.cv = tPage('apply.resumeRequired');
    if (!gdprConsent) errs.gdprConsent = tPage('apply.gdprConsentRequired');
    if (!aiConsent) errs.aiConsent = tPage('apply.aiConsentRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [firstName, lastName, email, phone, linkedIn, cvFile, gdprConsent, aiConsent, position, tPage]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');
    if (!validate() || !posting || !position || !company) return;

    setSubmitting(true);
    try {
      const consentTimestamp = new Date().toISOString();
      const formData = new FormData();
      formData.append('firstName', firstName);
      formData.append('lastName', lastName);
      formData.append('email', email);
      if (phone) formData.append('phone', phone);
      if (linkedIn) formData.append('linkedinUrl', linkedIn);
      formData.append('jobPostingId', posting.id);
      formData.append('companyId', company.id);
      formData.append('jobPositionId', position.id);
      formData.append('source', 'landing-page');
      formData.append('gdprConsentTimestamp', consentTimestamp);
      formData.append('aiConsentTimestamp', consentTimestamp);
      if (cvFile) formData.append('cv', cvFile);

      await api.public.submitApplication(formData);
      setPageState('success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('rate') || msg.includes('429')) setServerError(tPage('apply.rateLimit'));
      else if (msg.includes('duplicate') || msg.includes('409')) setServerError(tPage('apply.duplicate'));
      else if (msg.includes('phone')) setErrors(prev => ({ ...prev, phone: tPage('apply.phoneRequired') }));
      else if (msg.includes('VALIDATION_ERROR') || msg.includes('400')) setServerError(tPage('apply.validationError'));
      else setServerError(tPage('apply.genericError'));
    } finally {
      setSubmitting(false);
    }
  }, [validate, posting, position, company, firstName, lastName, email, phone, linkedIn, cvFile, gdprConsent, aiConsent, tPage]);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/pdf' && file.size <= 10 * 1024 * 1024) {
      setCvFile(file);
      setErrors(prev => { const n = { ...prev }; delete n.cv; return n; });
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf' && file.size <= 10 * 1024 * 1024) {
      setCvFile(file);
      setErrors(prev => { const n = { ...prev }; delete n.cv; return n; });
    }
  }, []);

  return {
    pageState, pageLanguage, tPage, submitting, errors, serverError,
    company, position, posting,
    firstName, setFirstName, lastName, setLastName,
    email, setEmail, phone, setPhone,
    linkedIn, setLinkedIn, cvFile, setCvFile,
    gdprConsent, setGdprConsent, aiConsent, setAiConsent,
    fileInputRef: fileInputRef as React.RefObject<HTMLInputElement>,
    handleSubmit, handleFileDrop, handleFileSelect,
  };
}
